import React, { useState, useCallback, useMemo, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import useWorkspaceChange from '../../../hooks/useWorkspaceChange';
import {
    Box,
    Card,
    Alert,
    Skeleton,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    IconButton,
    Tooltip,
    Typography,
} from "@mui/material";
import PersonIcon from "@mui/icons-material/Person";
import { useLanguage } from "../../../context/LanguageContext";
import { useClientsData } from "../../../context/ClientsDataContext";
import { PageContainer, PageTitle } from "../../../components/Common";
import { useSearchPersons } from "../../../hooks/Clients/search";
import { useStoreList } from "../../../context/StoreListContext";
import { useStoreData } from "../../../context/StoreDataContext";
import { CustomerSearchParams } from "../../../types/Customer";
import SearchForm, { SearchFormHandle } from "./SearchForm";
import { TooltipCell } from "../Dashboard/components/ui";

const SearchClients = (): React.ReactElement => {
    const { t } = useLanguage();
    const navigate = useNavigate();
    const { customers: allCustomers, isLoading, error, searchPersons, resetSearch } = useSearchPersons();
    const { identifierTypes, personAttributes, loading: refLoading } = useClientsData();
    const { stores } = useStoreList();
    const { wayTypes, countries } = useStoreData();

    const [hasSearched, setHasSearched] = useState(false);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const searchFormRef = useRef<SearchFormHandle>(null);

    // Vider les résultats et le formulaire quand le workspace change
    useWorkspaceChange(() => {
        resetSearch();
        setHasSearched(false);
        setPage(0);
        searchFormRef.current?.reset();
    });

    // Pagination client-side
    const customers = useMemo(
        () => allCustomers.slice(page * rowsPerPage, (page + 1) * rowsPerPage),
        [allCustomers, page, rowsPerPage]
    );
    const displayTotal = allCustomers.length;

    // Redirection automatique si un seul résultat
    useEffect(() => {
        if (hasSearched && !isLoading && allCustomers.length === 1) {
            const only = allCustomers[0];
            navigate(`/clients/dashboard/${only.PERSONID}`, { state: { personId: only.PERSONID } });
        }
    }, [hasSearched, isLoading, allCustomers, navigate]);

    // Callbacks stables passés à SearchForm — ne changent jamais entre les frappes
    const handleSearch = useCallback(
        async (params: CustomerSearchParams) => {
            setPage(0);
            setHasSearched(true);
            await searchPersons(params);
        },
        [searchPersons]
    );

    const handleReset = useCallback(() => {
        resetSearch();
        setHasSearched(false);
        setPage(0);
    }, [resetSearch]);

    const handlePageChange = useCallback((_: unknown, newPage: number) => {
        setPage(newPage);
    }, []);

    const handleRowsPerPageChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        setRowsPerPage(parseInt(e.target.value, 10));
        setPage(0);
    }, []);

    const handleRowClick = useCallback(
        (personId: string | number) => {
            navigate(`/clients/dashboard/${personId}`, { state: { personId } });
        },
        [navigate]
    );

    return (
        <PageContainer>
            <PageTitle>{t("pages.customers.searchTitle")}</PageTitle>

            {/* Formulaire isolé — ses re-renders (frappe clavier) ne touchent pas le tableau */}
            <SearchForm
                ref={searchFormRef}
                onSearch={handleSearch}
                onReset={handleReset}
                isLoading={isLoading}
                identifierTypes={identifierTypes}
                personAttributes={personAttributes}
                refLoading={refLoading}
                wayTypes={wayTypes}
                countries={countries}
                stores={stores}
            />

            {/* Erreur */}
            {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                    {error}
                </Alert>
            )}

            {/* Résultats */}
            {hasSearched && (
                <Card sx={{ overflow: "hidden" }}>
                    <TableContainer sx={{ overflowX: "hidden" }}>
                        <Table size="small" sx={{ tableLayout: 'fixed', width: '100%' }}>
                            <colgroup>
                                <col style={{ width: '13%' }} /> {/* Identifier */}
                                <col style={{ width: '16%' }} /> {/* Web identifier — masqué sur petits écrans */}
                                <col style={{ width: '13%' }} /> {/* Lastname */}
                                <col style={{ width: '11%' }} /> {/* Firstname */}
                                <col style={{ width: '16%' }} /> {/* Address */}
                                <col style={{ width: '9%' }} />  {/* Zip */}
                                <col style={{ width: '11%' }} /> {/* City */}
                                <col style={{ width: '11%' }} /> {/* Birthdate */}
                            </colgroup>
                            <TableHead>
                                <TableRow sx={{
                                    "& .MuiTableCell-head": {
                                        bgcolor: "grey.50",
                                        color: "text.secondary",
                                        fontWeight: 600,
                                        fontSize: "0.7rem",
                                        letterSpacing: "0.04em",
                                        textTransform: "uppercase",
                                        py: 1.25,
                                        whiteSpace: "nowrap",
                                        overflow: "hidden",
                                        textOverflow: "ellipsis",
                                        borderBottom: "1px solid",
                                        borderColor: "divider",
                                    },
                                    "& .MuiTableCell-body": {
                                        overflow: "hidden",
                                        textOverflow: "ellipsis",
                                        whiteSpace: "nowrap",
                                    }
                                }}>
                                    <TableCell>{t("pages.customers.searchResults.columns.identifier")}</TableCell>
                                    <TableCell sx={{ display: { xs: "none", lg: "table-cell" } }}>{t("pages.customers.searchResults.columns.webIdentifier")}</TableCell>
                                    <TableCell>{t("pages.customers.searchResults.columns.lastname")}</TableCell>
                                    <TableCell>{t("pages.customers.searchResults.columns.firstname")}</TableCell>
                                    <TableCell>{t("pages.customers.searchResults.columns.address")}</TableCell>
                                    <TableCell>
                                        <Box component="span" sx={{ display: { xs: "inline", lg: "none" } }}>CP</Box>
                                        <Box component="span" sx={{ display: { xs: "none", lg: "inline" } }}>{t("pages.customers.searchResults.columns.zipCode")}</Box>
                                    </TableCell>
                                    <TableCell>{t("pages.customers.searchResults.columns.city")}</TableCell>
                                    <TableCell>{t("pages.customers.searchResults.columns.birthDate")}</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {isLoading ? (
                                    Array.from({ length: rowsPerPage > 10 ? 10 : rowsPerPage }).map((_, i) => (
                                        <TableRow key={i}>
                                            <TableCell>
                                                <Skeleton variant="text" sx={{ fontSize: "0.875rem", maxWidth: 80 }} />
                                            </TableCell>
                                            <TableCell sx={{ display: { xs: "none", lg: "table-cell" } }}>
                                                <Skeleton variant="text" sx={{ fontSize: "0.875rem", maxWidth: 100 }} />
                                            </TableCell>
                                            {Array.from({ length: 6 }).map((__, j) => (
                                                <TableCell key={j}>
                                                    <Skeleton variant="text" sx={{ fontSize: "0.875rem", maxWidth: j === 1 ? 120 : 80 }} />
                                                </TableCell>
                                            ))}
                                        </TableRow>
                                    ))
                                ) : allCustomers.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={10} align="center" sx={{ py: 6, border: 0 }}>
                                            <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 1, color: "text.disabled" }}>
                                                <PersonIcon sx={{ fontSize: 40, opacity: 0.3 }} />
                                                <Typography variant="body2" color="text.disabled">
                                                    {t("pages.customers.searchResults.noResults")}
                                                </Typography>
                                            </Box>
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    customers.map((customer) => (
                                        <TableRow
                                            key={String(customer.PERSONID)}
                                            hover
                                            sx={{
                                                cursor: "pointer",
                                                "&:hover": { bgcolor: "action.hover" },
                                                "&:last-child td": { borderBottom: 0 },
                                            }}
                                            onClick={() => handleRowClick(customer.PERSONID)}
                                        >
                                            <TableCell>
                                                {customer.IDENTIFIER || customer.CARTE ? (
                                                    <Typography variant="caption" sx={{
                                                        fontFamily: "monospace",
                                                        bgcolor: "grey.100",
                                                        px: 0.8,
                                                        py: 0.3,
                                                        borderRadius: 0.75,
                                                        color: "text.primary",
                                                        letterSpacing: "0.03em",
                                                        fontWeight: 500,
                                                    }}>
                                                        {String(customer.IDENTIFIER || customer.CARTE)}
                                                    </Typography>
                                                ) : <Typography variant="body2" color="text.disabled">—</Typography>}
                                            </TableCell>
                                            <TooltipCell
                                                label={customer.WEB_IDENTIFIER || ""}
                                                sx={{ color: "text.secondary", fontSize: "0.8rem", display: { xs: "none", lg: "table-cell" } }}
                                            >
                                                {customer.WEB_IDENTIFIER || "—"}
                                            </TooltipCell>
                                            <TableCell sx={{ fontWeight: 500, color: "text.primary" }}>{customer.LASTNAME || "—"}</TableCell>
                                            <TableCell sx={{ color: "text.secondary" }}>{customer.FIRSTNAME || "—"}</TableCell>
                                            <TooltipCell
                                                label={customer.ADDRESS3 || customer.ADDRESS1 || ""}
                                                sx={{ color: "text.secondary", fontSize: "0.8rem" }}
                                            >
                                                {customer.ADDRESS3 || customer.ADDRESS1 || "—"}
                                            </TooltipCell>
                                            <TableCell sx={{ color: "text.secondary", fontSize: "0.8rem" }}>
                                                {customer.ZIP_CODE || "—"}
                                            </TableCell>
                                            <TableCell sx={{ color: "text.secondary" }}>{customer.CITY || "—"}</TableCell>
                                            <TableCell sx={{ color: "text.secondary", fontSize: "0.8rem" }}>
                                                {customer.BIRTH_DATE || "—"}
                                            </TableCell>
                                        </TableRow>
                                    ))
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    <TablePagination
                        component="div"
                        count={displayTotal}
                        page={page}
                        onPageChange={handlePageChange}
                        rowsPerPage={rowsPerPage}
                        onRowsPerPageChange={handleRowsPerPageChange}
                        rowsPerPageOptions={[10, 20, 50]}
                        labelRowsPerPage={t("common.rowsPerPage")}
                        labelDisplayedRows={({ from, to, count }) => `${from}–${to} ${t("common.of")} ${count}`}
                    />
                </Card>
            )}
        </PageContainer>
    );
};

export default SearchClients;
